import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { Loader2, KeyRound, AlertCircle } from 'lucide-react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LOGO_URL = "https://kroytechnologygroup.com/wp-content/uploads/2022/01/KTG-Final-Logo-Flat-300x200.png";

export default function AcceptInvitePage() {
  const { token } = useParams();
  const navigate = useNavigate();
  const { setAuthData } = useAuth();

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [invite, setInvite] = useState(null);
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  useEffect(() => {
    loadInvite();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const loadInvite = async () => {
    try {
      const res = await axios.get(`${API_URL}/auth/invite/${token}`);
      setInvite(res.data);
    } catch {
      setError('This invitation link is invalid or has already been used.');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }
    if (password !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    setSubmitting(true);
    try {
      const res = await axios.post(`${API_URL}/auth/accept-invite`, {
        token,
        password
      });
      setAuthData(res.data.access_token, res.data.user);
      toast.success('Account created! Welcome to AuditGuard.');
      navigate('/dashboard');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create account');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
        <div className="w-12 h-12 border-4 border-[#FF6B35] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 p-4">
        <div className="w-full max-w-md text-center">
          <div className="bg-white rounded-2xl shadow-2xl p-8">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <AlertCircle className="h-8 w-8 text-red-600" />
            </div>
            <h1 className="text-xl font-bold text-slate-900 mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>Invalid Invitation</h1>
            <p className="text-slate-600 mb-6">{error}</p>
            <Link to="/login">
              <Button className="btn-primary">Go to Login</Button>
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 p-4">
      <div className="w-full max-w-md">
        <div className="bg-white rounded-2xl shadow-2xl p-8">
          <img src={LOGO_URL} alt="AuditGuard" className="h-16 w-auto rounded-xl mx-auto mb-6 bg-white p-1" />
          
          <div className="text-center mb-6">
            <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Welcome, {invite.contact_name}!
            </h1>
            <p className="text-slate-500 mt-2">
              Set your password to access the <strong>{invite.company_name}</strong> compliance portal.
            </p>
          </div>

          <div className="bg-slate-50 rounded-lg p-3 mb-6 text-sm">
            <p className="text-slate-600"><strong>Email:</strong> {invite.email}</p>
            <p className="text-slate-600"><strong>Company:</strong> {invite.company_name}</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={e => setPassword(e.target.value)}
                placeholder="Minimum 8 characters"
                required
                data-testid="invite-password"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="confirm">Confirm Password</Label>
              <Input
                id="confirm"
                type="password"
                value={confirmPassword}
                onChange={e => setConfirmPassword(e.target.value)}
                placeholder="Re-enter your password"
                required
                data-testid="invite-confirm-password"
              />
            </div>
            <Button type="submit" className="w-full btn-primary py-3" disabled={submitting} data-testid="invite-submit-btn">
              {submitting
                ? <><Loader2 className="h-4 w-4 animate-spin mr-2" />Creating Account...</>
                : <><KeyRound className="h-4 w-4 mr-2" />Create Account & Login</>
              }
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
}
